import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Truck, Train, PackageCheck, X as CloseIcon, Check } from 'lucide-react';
import { getNotifications, markNotificationsRead } from '../lib/api';

const TYPE_META = {
  port_truck_exit: { icon: Truck, color: 'text-amber-500', bg: 'bg-amber-500/10' },
  ferrovalle_arrival: { icon: Train, color: 'text-green-500', bg: 'bg-green-500/10' },
  delivery_suggested: { icon: PackageCheck, color: 'text-blue-500', bg: 'bg-blue-500/10' },
};

function formatRelative(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `hace ${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `hace ${hours}h`;
  const days = Math.floor(hours / 24);
  return `hace ${days}d`;
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const res = await getNotifications({ limit: 50 });
      setItems(res.data.items || []);
      setUnread(res.data.unread_count || 0);
    } catch (e) {
      // silent — bell just shows 0
    }
  }, []);

  // Poll every 60s. Cheap (small response) and keeps the badge fresh without websockets.
  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const handleOpen = async () => {
    setOpen(true);
    await load();
  };

  const handleItemClick = async (n) => {
    if (!n.read) {
      try {
        await markNotificationsRead([n.id]);
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
        setUnread((u) => Math.max(0, u - 1));
      } catch { /* ignore */ }
    }
    setOpen(false);
    if (n.shipment_mbl) {
      navigate(`/shipments/${encodeURIComponent(n.shipment_mbl)}`);
    }
  };

  const markAllRead = async () => {
    try {
      await markNotificationsRead();
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
      setUnread(0);
    } catch { /* ignore */ }
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={handleOpen}
        className="relative p-2 rounded-md text-foreground/70 hover:text-foreground hover:bg-muted/60 transition-colors"
        data-testid="notification-bell"
        aria-label="Notificaciones"
      >
        <Bell size={20} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-semibold rounded-full bg-destructive text-destructive-foreground"
            data-testid="notification-badge"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[360px] max-h-[520px] bg-popover border border-border rounded-lg shadow-xl z-50 overflow-hidden flex flex-col"
          data-testid="notification-panel"
        >
          <div className="p-3 border-b border-border flex items-center justify-between">
            <div>
              <h4 className="font-heading text-sm text-foreground">Notificaciones</h4>
              <p className="text-xs text-muted-foreground">{unread} sin leer</p>
            </div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  type="button"
                  onClick={markAllRead}
                  className="text-xs px-2 py-1 rounded hover:bg-muted text-foreground/70 hover:text-foreground"
                  data-testid="mark-all-read"
                  title="Marcar todo como leído"
                >
                  <Check size={14} />
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-xs px-2 py-1 rounded hover:bg-muted text-foreground/70 hover:text-foreground"
                aria-label="Cerrar"
              >
                <CloseIcon size={14} />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {items.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                Todo al día — sin notificaciones nuevas.
              </div>
            ) : (
              items.map((n) => {
                const meta = TYPE_META[n.type] || { icon: Bell, color: 'text-muted-foreground', bg: 'bg-muted/40' };
                const Icon = meta.icon;
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => handleItemClick(n)}
                    className={`w-full text-left p-3 border-b border-border/50 flex gap-3 hover:bg-muted/40 transition-colors ${
                      !n.read ? 'bg-primary/5' : ''
                    }`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${meta.bg} ${meta.color}`}>
                      <Icon size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-sm font-medium text-foreground truncate">{n.title}</span>
                        {!n.read && <span className="shrink-0 w-2 h-2 rounded-full bg-primary mt-1.5" />}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.message}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {n.shipment_mbl && (
                          <span className="text-[10px] font-mono text-muted-foreground/80">{n.shipment_mbl}</span>
                        )}
                        <span className="text-[10px] text-muted-foreground">{formatRelative(n.created_at)}</span>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
